import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  Typography,
  Box,
  Modal,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Grid,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import { PersonOutlined, AccessTime, LocationOn } from '@mui/icons-material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Container } from '@mui/system';
import { Link, useParams } from 'react-router-dom';
import TimezoneSelector from '../../util/timezoneSelector';
import { DescriptionComponent, InfoRow, formatDuration } from '../../util/utils';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { Phone } from '@mui/icons-material';
import { MonthYearSelector } from '../../components/business/calendar/month/monthYearSelector';
import { UserContext } from '../../components/auth/auth-context';
import { CalendarProvider, useCalendar } from '../../components/business/calendar/calendarContext';
import BookingTimeSlotContainer from '../../components/business/calendar/week/bookingTimeSlotContainer';
import { QRCodeCanvas } from 'qrcode.react';
import { rootClientPath } from '../../util/link';

// Modal style
const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  maxHeight: '80vh',
  overflow: 'auto',
};

// Main component for Appointment Booking
const AppointmentBookingContent = ({ selectedBookingData, selectedBookingDay, selectedBookingMonth, businessUrl }) => {
  const [viewType, setViewType] = useState("month"); // Default view is "month"
  const [showQRCode, setShowQRCode] = useState(false);
  // State for dropdown menu anchor element
  const [anchorEl, setAnchorEl] = useState(null);

  // Select a random available staff member. 
  const randomIndex = Math.floor(Math.random() * selectedBookingData.assignedStaffs.length);
  const randomSelectedStaffId = selectedBookingData.assignedStaffs[randomIndex].staffId;
  const { bookingTypeLink } = useParams();

  const { providerUser } = useContext(UserContext);
  const [selectedStaffId, setSelectedStaffId] = useState(randomSelectedStaffId);
  const [openModal, setOpenModal] = useState(false);
  const { setCurrentMonth, setSelectedDate } = useCalendar();
  const [selectedTimeZone, setSelectedTimeZone] = useState(providerUser?.timeZone?.timezone);

  // Handle view change (month/week)
  const handleViewChange = (event) => {
    setViewType(event.target.value);
  };

  // Handle timezone selection
  const handleTimezoneSelect = useCallback((timezone) => {
    setSelectedTimeZone(timezone?.timezone);
  }, []);

  // Set selected date and month if available
  selectedBookingDay && setSelectedDate(selectedBookingDay);
  selectedBookingMonth && setCurrentMonth(selectedBookingMonth);

  // Handle staff selection from modal
  const handleStaffSelect = async (staffId) => {
    setSelectedStaffId(staffId);
    setOpenModal(false);
  };

  // Find the selected staff
  const selectedStaff = selectedStaffId && selectedStaffId !== "All"
    ? selectedBookingData.assignedStaffs?.find(staff => staff.staffId === selectedStaffId)
    : null;

  // Create the full booking page URL for the QR code.
  const bookingPageUrl = `${rootClientPath}/${businessUrl}/booking/${bookingTypeLink}`;

  // Dropdown menu handlers
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const toggleQRCode = () => {
    setShowQRCode(prev => !prev);
    handleMenuClose();
  };

  return (
    // Outer container with relative positioning for the dropdown
    <Box
      sx={{
        position: 'relative',
        maxWidth: 800,
        margin: '0 auto',
        padding: 2,
        backgroundColor: '#fff',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}
    >


      <Box
        sx={{
          maxWidth: 800,
          margin: '0 auto',
          padding: 2,
          backgroundColor: '#fff',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        {/* Provider and Company Information */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: { xs: 'center', sm: 'flex-start' },
              textAlign: { xs: 'center', sm: 'left' },
            }}
          >
            <Typography variant="body2" sx={{ color: '#667', mb: 0.5 }}>
              Provider
            </Typography>
            {selectedStaff ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar src={selectedStaff.image?.url} alt={selectedStaff.name} />
                <Typography variant="subtitle1">{selectedStaff.name}</Typography>
              </Box>
            ) : (
              <Typography variant="body1" color="text.secondary">
                No preference
              </Typography>
            )}
            {selectedStaff?.address && (
              <Typography variant="body2" color="text.secondary">
                {selectedStaff.address}
              </Typography>
            )}
            {selectedBookingData?.assignedStaffs?.length > 1 && (
              <Button
                variant="outlined"
                onClick={() => setOpenModal(true)}
                startIcon={<PersonOutlined />}
                sx={{
                  textTransform: 'none',
                  borderRadius: 1.5,
                  px: 3,
                  mt: 1,
                }}
              >
                Change
              </Button>
            )}
          </Box>
          {selectedStaff?.business?.name && (
            <Link
              to={`/${businessUrl}`}
              underline="none"
              sx={{
                fontSize: '1rem',
                color: 'primary.main',
                textAlign: { xs: 'center', sm: 'right' },
              }}
            >
              {selectedStaff.business.name}
            </Link>
          )}
        </Box>

        {/* Main Content */}
        <Grid container spacing={2}>
          {/* Title and Details */}
          <Grid item xs={12}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 'bold',
                fontSize: { xs: '1rem', sm: '1.3rem' },
                textAlign: 'left',
                mb: 2,
                overflowWrap: 'break-word',
              }}
            >
              {selectedBookingData.title}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <InfoRow icon={<AccessTime />} text={formatDuration(selectedBookingData?.length)} />
              {selectedBookingData?.location && (
                <InfoRow icon={<LocationOn />} text={selectedBookingData?.location} />
              )}
              {Number(selectedBookingData?.meetingLocationTypeId) === 2 && (
                <InfoRow icon={<Phone />} text="Check your email/SMS for the phone number." />
              )}
              {selectedBookingData?.price && (
                <InfoRow icon={<AttachMoneyIcon />} text={`$ ${selectedBookingData?.price}`} />
              )}
            </Box>
          </Grid>

          {/* Description */}
          {selectedBookingData?.desc && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <EventNoteIcon sx={{ fontSize: 20, mr: 1 }} />
                <Box sx={{ flexGrow: 1 }}>
                  <DescriptionComponent
                    selectedBookingData={{
                      desc: selectedBookingData.desc,
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* QR Code Section (position remains unchanged) */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        {showQRCode && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Scan to Book
            </Typography>
            <QRCodeCanvas value={bookingPageUrl} size={128} />
          </>
        )}
      </Box>

      {/* Timezone Selector */}

      {/* Outer container with relative positioning */}
      <Box sx={{ position: 'relative', mt: 1, px: 2 }}>
        {/* First row: Menu aligned to the right */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={toggleQRCode}>
              {showQRCode ? 'Hide QR Code' : 'Show QR Code'}
            </MenuItem>
            <MenuItem>
              <FormControl component="fieldset" sx={{ m: 0, p: 0 }}>
                <RadioGroup
                  row
                  value={viewType}
                  onChange={handleViewChange}
                  aria-label="view type"
                  name="viewType"
                  sx={{ fontSize: 'small' }}
                >
                  <FormControlLabel
                    value="month"
                    control={<Radio size="small" />}
                    label="Month"
                    sx={{ fontSize: 'small' }}
                  />
                  <FormControlLabel
                    value="week"
                    control={<Radio size="small" />}
                    label="Week"
                    sx={{ fontSize: 'small' }}
                  />
                </RadioGroup>
              </FormControl>
            </MenuItem>
          </Menu>
        </Box>

        {/* Second row: TimezoneSelector aligned to the left */}

      </Box>


      {/* Modal for Staff Selection */}
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box sx={modalStyle}>
          <Typography variant="h6" sx={{ mb: 2 }}>Select Staff</Typography>
          <List>
            <ListItem button onClick={() => handleStaffSelect("All")} selected={selectedStaffId === "All"}>
              <ListItemText primary="All Staff" />
            </ListItem>
            {selectedBookingData.assignedStaffs?.map((staff) => (
              <ListItem button key={staff.id} onClick={() => handleStaffSelect(staff.id)} selected={selectedStaffId === staff.staffId}>
                <ListItemAvatar>
                  <Avatar src={staff.image?.url} alt={staff.name} />
                </ListItemAvatar>
                <ListItemText primary={staff.name} secondary={staff.address} />
              </ListItem>
            ))}
          </List>
        </Box>
      </Modal>

      <Container
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        {/* MonthYearSelector centered at the top */}


        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column', // always stack vertically
            alignItems: 'center', 
            pr: 1, // reduced right padding  
            p: 0.5, // reduced overall padding   
          }}
        >
          {/* Menu Icon placed at the top right corner */}
          <IconButton
            onClick={handleMenuOpen}
            sx={{
              position: 'absolute',
              top: 1,
              right: 1,
              zIndex: 10,
            }}
          >
            <MoreVertIcon />
          </IconButton>

          {/* MonthYearSelector: full width on small screens, centered */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column', // always stack vertically
            }}
          >
            <MonthYearSelector checkavailability={true} bookingType={selectedBookingData} />

            <FormControl
              sx={{
                justifyContent: 'center',
                minWidth: 450,
                mt: 1, // moves the selector down
                '& .MuiInputBase-root': {
                  fontSize: '0.7rem', // smaller font size
                  fontWeight: 'normal', // not bold
                  cursor: 'pointer', // indicate clickable
                  transition: 'border-color 0.3s',
                  '&:hover': {
                    borderColor: 'primary.main', // change border color on hover
                  },
                },
              }}
            >
              <TimezoneSelector
                timezoneId={selectedTimeZone}
                onTimezoneSelect={handleTimezoneSelect}
              />
            </FormControl>
          </Box>

        </Box>


        {/* BookingTimeSlotContainer placed below */}
        <BookingTimeSlotContainer
          isTimeSlotVisible={true}
          bookingType={selectedBookingData}
          selectedStaffId={selectedStaffId}
          userTimezone={selectedTimeZone}
          viewType={viewType}
          businessUrl={businessUrl}
        />
      </Container>
    </Box>
  );
};

const AppointmentBooking = (props) => {
  return (
    <CalendarProvider>
      <AppointmentBookingContent {...props} />
    </CalendarProvider>
  );
};

export default AppointmentBooking;
